import { AnimatedList } from "@neucleos/motion-primitives";
import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";

/**
 * A parked group in the Room Rail (RM-CAN-10 law 5): a disclosure header in
 * the section's micro-label voice with a count, over the estate's `.t-acc`
 * fold (index.css, already seated) holding ONE AnimatedList frame — static
 * cards, scroll-driven fades and settle, no entrance (FD-CAN-38) — capped at
 * `maxRows` rows. The open/closed latch is a per-viewer convenience in
 * localStorage, never World truth; storage refusal never breaks the group.
 */
const ROW_PX = 30;

function readLatch(key: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw === "1";
  } catch {
    return fallback;
  }
}

function writeLatch(key: string, open: boolean): void {
  try {
    window.localStorage.setItem(key, open ? "1" : "0");
  } catch {
    // A viewer convenience only.
  }
}

export function RailGroup<T>({
  name,
  label,
  items,
  keyOf,
  renderItem,
  latchKey,
  defaultOpen = true,
  maxRows = 6,
  emptyState = null,
}: {
  /** The group's `data-room-rail-<name>` hook and the frame's `data-cleo`. */
  readonly name: string;
  readonly label: string;
  readonly items: ReadonlyArray<T>;
  readonly keyOf: (item: T, index: number) => string;
  readonly renderItem: (item: T, index: number) => ReactNode;
  readonly latchKey: string;
  readonly defaultOpen?: boolean;
  readonly maxRows?: number;
  readonly emptyState?: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(() =>
    typeof window === "undefined" ? defaultOpen : readLatch(latchKey, defaultOpen),
  );
  const toggle = () => {
    setOpen((prev) => {
      writeLatch(latchKey, !prev);
      return !prev;
    });
  };
  return (
    <div
      className="flex w-full min-w-0 flex-none flex-col"
      data-open={open ? "true" : "false"}
      {...{ [`data-room-rail-${name}`]: String(items.length) }}
    >
      <div
        data-room-rail-fold={name}
        aria-expanded={open}
        className="flex h-6 w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-lg px-1 outline-hidden transition-colors hover:bg-accent/30 focus-visible:ring-1 focus-visible:ring-ring"
        data-room-rail-group-head={name}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </span>
        <span className="font-mono text-[9px] leading-none text-muted-foreground">
          {items.length}
        </span>
      </div>
      <div className="t-acc" data-open={open ? "true" : "false"}>
        <div className="t-acc-inner">
          <div
            className="flex min-h-0 flex-col"
            data-room-rail-group-frame={name}
            style={
              {
                maxHeight: `${maxRows * ROW_PX}px`,
                "--alist-gap": "6px",
                "--alist-pad": "4px 2px 12px",
              } as CSSProperties
            }
          >
            <AnimatedList
              ariaLabel={label}
              cleo={`room-rail-${name}`}
              emptyState={emptyState}
              enableArrowNavigation={false}
              items={items}
              keyOf={keyOf}
              renderItem={(item, index) => renderItem(item, index)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
